import * as React from 'react';
import Axios from 'axios';
import {SortableContainer, SortableElement, SortEnd, SortEvent, arrayMove} from 'react-sortable-hoc';

import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

import { withAppContext, IAppContext } from '../../app-context';
import { LogoSizeVariant, LogoColorVariant } from '../../logo.types';
import { LogoGalleryImage, ILogoGalleryImageData } from './logo-gallery-image.component';

import './logo-gallery.component.css';
import SimpleMessageDialog from '../simple-message.dialog';
import ConfirmLogoDeleteDialog from './confirm-logo-delete.dialog';
import { localization } from '../../localization/localization';
import { DebuggerInstance } from '../../debugger';

//#region React-sortable

export interface ISortableItemProps {
    image: ILogoGalleryImageData;
    logoTitle?: string;
}

/**
 * Component representing one sortable item in list
 * handles also visualization of selected item
 */
const SortableItem = SortableElement<ISortableItemProps>((props: ISortableItemProps) =>
    // visualization of selected item must be set here, because on lower levels parent is not changed and components are not updated
    <div className={ 'box ' + (props.image.showSelected ? 'logo-image-selected' : '')}>
        <LogoGalleryImage {...props} />
    </div>
);

export interface ISortableContainerProps {
    images: ILogoGalleryImageData[];
    logoTitle?: string;
}
const SortableList = SortableContainer<ISortableContainerProps>((props: ISortableContainerProps) => {
  return (
    <div className="logo-gallery-container">
      {props.images.map((value, index) => (
        <SortableItem key={`item-${index}`} index={index} image={value} logoTitle={props.logoTitle} />
      ))}
    </div>
  );
});

//#endregion

export interface ILogoGalleryComponentProps {
    appContext: IAppContext;
    showDeleteBtn: boolean;
    excludeMotives: boolean;
    onLogoSelected(logoId: string): void;
    onEmptyGallery(): void;
    logoTitle?: string;
}

export interface ILogoGalleryComponentState {
    userLogos: ILogoGalleryImageData[];
    globalLogos: ILogoGalleryImageData[];
    isLoading: boolean;
}

export interface ILogoInfo {
    logoId: string;
    sort: number;
    isMotive: boolean;
    isGlobal: boolean;
    format: string;
    created: Date | null;
    useDarkBackground: boolean;
}

export interface IGalleryLogos {
    userLogos: ILogoInfo[];
    globalLogos: ILogoInfo[];
}

export interface ILogoSortInfo {
    logoId: string;
    sort: number;
}

/**
 * Razeni pozic obrazku je na zaklade indexu v 'images'
 */
class LogoGalleryComponent extends React.PureComponent<ILogoGalleryComponentProps,ILogoGalleryComponentState> {
    private apiUrl: string;

    constructor(props: ILogoGalleryComponentProps) {
        super(props);
        this.state = {
            userLogos: [],
            globalLogos: [],
            isLoading: false
        };

        DebuggerInstance.log('LogoGalleryComponent - constructor');
        const apiKey = props.appContext.getApiKey();
        DebuggerInstance.log(apiKey);
        const userId = props.appContext.getUserId();
        DebuggerInstance.log(userId);
        this.apiUrl = `/api/${apiKey}/user/${userId}/logo-upload/`;
        DebuggerInstance.log(this.apiUrl);
    }

    public componentDidMount() {
        this.loadLogos();
        this.props.appContext.raiseTronlogoEvent({
            type: 'OPENGALLERY'
        });
    }

    private async loadLogos() {
        DebuggerInstance.log('LogoGalleryComponent - loadLogos');

        this.setState({ userLogos: [], isLoading: true });

        let userLogos: ILogoGalleryImageData[] = [];
        let globalLogos: ILogoGalleryImageData[] = [];
        try {
            const response = await Axios.get<IGalleryLogos>(this.apiUrl);
            if (response.data.userLogos.length === 0 && response.data.globalLogos.length === 0) {
                this.handleEmptyGallery();
                return;
            }

            const selectedLogo: string | null = this.props.appContext.getLogoId();
            userLogos = this.getGalleryLogos(response.data.userLogos, selectedLogo);
            globalLogos = this.getGalleryLogos(response.data.globalLogos, selectedLogo);
        } catch(error) {
            this.props.appContext.openDialog(
                <SimpleMessageDialog
                    title={localization.galleryLoc.loadFailedTtile}
                    message={localization.galleryLoc.loadFailedMessage}
                    buttonText={localization.commonLoc.close}
                    onClick={() => this.props.appContext.closeDialog()} />
            );
        }

        this.setState({
            userLogos,
            globalLogos,
            isLoading: false
        });

        this.scrollToSelectedImage();
    }

    private getGalleryLogos(logos: ILogoInfo[], selectedLogo: string | null) {
        const result: ILogoGalleryImageData[] = [];
        const sortedLogos = logos.sort((x, y) => {
            if (x.sort < y.sort) {
                return 1; }
            if (x.sort > y.sort) {
                return -1; }
            return 0;
        });

        sortedLogos.forEach(logo => {
            if (this.props.excludeMotives && logo.isMotive) {
                return;
            }

            const logoData: ILogoGalleryImageData = {
                src: this.apiUrl + logo.logoId + '?color_variant=' + LogoColorVariant.normal + '&size_variant=' + LogoSizeVariant.Preview,
                id: logo.logoId,
                isMotive: logo.isMotive,
                isGlobal: logo.isGlobal,
                showDeleteBtn: this.props.showDeleteBtn && !logo.isGlobal,
                onImageDelete: (id) => this.handleLogoDelete(id),
                showSelected: selectedLogo != null && selectedLogo === logo.logoId,
                onSelection: (id) => this.handleLogoSelected(id),
                format: logo.format,
                created: logo.created,
                useDarkBackground: logo.useDarkBackground
            }

            result.push(logoData);
        })

        return result;
    }
    
    private handleEmptyGallery(): void {
        this.props.onEmptyGallery();
    }

    private handleLogoSelected(id: string): void {
        const nextImages: ILogoGalleryImageData[] = [];
        this.state.userLogos.forEach((val) => {
            val.showSelected = (val.id === id);
            nextImages.push(val);
        });

        this.setState({
            userLogos: nextImages
        });

        this.props.onLogoSelected(id);
    }

    private handleLogoDelete(id: string): void {
        this.props.appContext.openDialog(
            <ConfirmLogoDeleteDialog
                logoId={id}
                onKeep={() => this.props.appContext.closeDialog()}
                onDelete={(logo) => {
                    this.deleteLogo(logo);
                    this.props.appContext.closeDialog();
                }}
            />
        );
    }

    private async deleteLogo(id: string) {
        this.props.appContext.closeDialog();

        try {
            await Axios.delete(this.apiUrl + id);

            // handle for situation when removing selected logo
            const selectedLogo: string | null = this.props.appContext.getLogoId();
            if (selectedLogo && selectedLogo === id) {
                this.props.appContext.onLogochanged('');
            }
        } catch(error) {
            this.props.appContext.openDialog(
                <SimpleMessageDialog
                    title={localization.galleryLoc.deleteFailedTtile}
                    message={localization.galleryLoc.daleteFailedMessage} 
                    buttonText={localization.commonLoc.close}
                    onClick={() => this.props.appContext.closeDialog()} />
            );
            return;
        }

        const images = this.state.userLogos.filter(item => item.id !== id);
        this.setState({
            userLogos: images
        });

        // check if gallery is not empty now
        if (images.length === 0) {
            this.handleEmptyGallery();
        }
    }

    private scrollToSelectedImage() {
        const elements : any = document.getElementsByClassName("logo-image-selected");
        if (elements && elements.length > 0) {
            elements[0].scrollIntoView({behavior: "smooth", block: "end", inline: "nearest"});
        }
    }

    private handleOnSortEnd(sort: SortEnd, event: SortEvent): void {
        if (sort.newIndex === sort.oldIndex) {
            return; // no change
        }

        const images = arrayMove(this.state.userLogos, sort.oldIndex, sort.newIndex);

        const imagesCount = images.length;
        const updates: ILogoSortInfo[] = images.filter(c => c.isGlobal === false).map((val, index) =>({logoId: val.id, sort: imagesCount - index}));
        Axios.patch<void>(this.apiUrl, updates);

        this.setState({userLogos: images});
    }

    public render() {
        return (
            <div className="logo-gallery-root">
                <SimpleBar className="logo-gallery-content">
                    <SortableList
                        images={this.state.userLogos}
                        onSortEnd={(sort, event) => this.handleOnSortEnd(sort, event)}
                        axis='xy'
                        lockToContainerEdges={true}
                        useDragHandle={true}
                        logoTitle={this.props.logoTitle}
                        />
                    <div className="gallery-separator">{localization.galleryLoc.defaultLogoSeparator}</div>
                    <SortableList
                        images={this.state.globalLogos}
                        axis='xy'
                        lockToContainerEdges={true}
                        useDragHandle={true}
                        logoTitle={this.props.logoTitle}
                        />
                </SimpleBar>
            </div>
        );
    }
}

export const LogoGallery = withAppContext(LogoGalleryComponent);



// WEBPACK FOOTER //
// ./src/App/shared/components/gallery/logo-gallery.component.tsx